import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import PrintIcon from "@mui/icons-material/PrintOutlined";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { API_BASE } from "config/api";

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const TYPE_LABELS = {
  baptism: "Baptism",
  confirmation: "Confirmation",
  marriage: "Marriage",
  funeral: "Funeral",
};
const TYPE_COLORS = {
  baptism: "info",
  confirmation: "primary",
  marriage: "success",
  other: "default",
};
const TypeCell = ({ value }) => (
  <StatusChip value={TYPE_LABELS[value] || value} color={TYPE_COLORS[value] || "default"} />
);
TypeCell.propTypes = { value: PropTypes.string.isRequired };

const PersonCell = ({ row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {row.original.personName}
    </Typography>
    {row.original.secondPersonName && (
      <Typography variant="caption" color="text.secondary">
        &amp; {row.original.secondPersonName}
      </Typography>
    )}
  </Box>
);
PersonCell.propTypes = { row: PropTypes.shape({ original: PropTypes.object }).isRequired };

const ActionsCell = ({ row, handleEdit, handleDelete, handleCertificate }) => (
  <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
    <Button
      size="small"
      variant="outlined"
      color="info"
      startIcon={<PrintIcon fontSize="small" />}
      onClick={() => handleCertificate(row.original)}
    >
      Certificate
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleCertificate: PropTypes.func.isRequired,
};

const emptyForm = () => ({
  id: null,
  type: "baptism",
  primary_member_id: null,
  primaryLabel: "",
  spouse_member_id: null,
  spouseLabel: "",
  spouse_name: "",
  record_date: new Date().toISOString().split("T")[0],
  officiant_name: "",
  certificate_number: "",
  location: "",
  notes: "",
});

export default function useSacramentalRecordData() {
  const { church } = useAuth();

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [certificateModalOpen, setCertificateModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [isSaving, setIsSaving] = useState(false);

  const [primarySearch, setPrimarySearch] = useState("");
  const [primaryResults, setPrimaryResults] = useState([]);
  const [primarySelectedLabel, setPrimarySelectedLabel] = useState("");
  const [spouseSearch, setSpouseSearch] = useState("");
  const [spouseResults, setSpouseResults] = useState([]);
  const [spouseSelectedLabel, setSpouseSelectedLabel] = useState("");

  const authHeaders = () => {
    const token = localStorage.getItem("authToken");
    return token ? { Accept: "application/json", Authorization: `Bearer ${token}` } : null;
  };

  const handleEdit = useCallback((row) => {
    const raw = row.originalData || {};
    setForm({
      id: row.id,
      type: raw.type || "baptism",
      primary_member_id: raw.primary_member_id || null,
      primaryLabel: raw.primary_member
        ? `${raw.primary_member.first_name} ${raw.primary_member.surname}`
        : "",
      spouse_member_id: raw.spouse_member_id || null,
      spouseLabel: raw.spouse_member
        ? `${raw.spouse_member.first_name} ${raw.spouse_member.surname}`
        : "",
      spouse_name: raw.spouse_name || "",
      record_date: raw.record_date ? raw.record_date.split("T")[0] : "",
      officiant_name: raw.officiant_name || "",
      certificate_number: raw.certificate_number || "",
      location: raw.location || "",
      notes: raw.notes || "",
    });
    const primaryLabel = raw.primary_member
      ? `${raw.primary_member.first_name} ${raw.primary_member.surname}`
      : "";
    const spouseLabel = raw.spouse_member
      ? `${raw.spouse_member.first_name} ${raw.spouse_member.surname}`
      : "";
    setPrimarySearch(primaryLabel);
    setPrimarySelectedLabel(primaryLabel);
    setSpouseSearch(spouseLabel);
    setSpouseSelectedLabel(spouseLabel);
    setPrimaryResults([]);
    setSpouseResults([]);
    setEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((row) => {
    setSelectedRecord(row);
    setDeleteModalOpen(true);
  }, []);

  const handleCertificate = useCallback((row) => {
    setSelectedRecord(row);
    setCertificateModalOpen(true);
  }, []);

  const columns = [
    { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
    { Header: "Type", accessor: "type", align: "center", width: "12%", Cell: TypeCell },
    { Header: "Person(s)", accessor: "personName", align: "left", width: "20%", Cell: PersonCell },
    { Header: "Date", accessor: "dateLabel", align: "center", width: "12%" },
    { Header: "Officiant", accessor: "officiantName", align: "left", width: "15%" },
    { Header: "Certificate #", accessor: "certificateNumber", align: "left", width: "13%" },
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      width: "23%",
      Cell: (props) => (
        <ActionsCell
          {...props}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleCertificate={handleCertificate}
        />
      ),
    },
  ];

  const fetchRecords = useCallback(async () => {
    if (!church?.id) return;
    setIsLoading(true);
    try {
      const headers = authHeaders();
      if (!headers) return;
      const qs = typeFilter ? `?type=${typeFilter}` : "";
      const response = await fetch(`${API_BASE}/sacramental-records${qs}`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const records = result.data || [];
      setRows(
        records.map((r, index) => ({
          id: r.id,
          serial: index + 1,
          type: r.type,
          personName: r.primary_member
            ? `${r.primary_member.first_name} ${r.primary_member.surname}`
            : "—",
          secondPersonName: r.spouse_member
            ? `${r.spouse_member.first_name} ${r.spouse_member.surname}`
            : r.spouse_name || null,
          dateLabel: new Date(r.record_date).toLocaleDateString(),
          officiantName: r.officiant_name || "—",
          certificateNumber: r.certificate_number || "—",
          originalData: r,
        }))
      );
      setError(null);
    } catch (e) {
      console.error("Fetch sacramental records failed:", e);
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [church?.id, typeFilter]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const searchMembers = async (query, setResults) => {
    if (!church?.id || !query.trim()) {
      setResults([]);
      return;
    }
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(
        `${API_BASE}/members/${church.id}/search?query=${encodeURIComponent(query)}`,
        { headers }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setResults(result.data || []);
    } catch (e) {
      console.error("Member search failed:", e);
      setResults([]);
    }
  };

  useEffect(() => {
    if (!primarySearch.trim() || primarySearch === primarySelectedLabel) {
      setPrimaryResults([]);
      return;
    }
    const timer = setTimeout(() => searchMembers(primarySearch, setPrimaryResults), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [primarySearch]);

  useEffect(() => {
    if (!spouseSearch.trim() || spouseSearch === spouseSelectedLabel) {
      setSpouseResults([]);
      return;
    }
    const timer = setTimeout(() => searchMembers(spouseSearch, setSpouseResults), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [spouseSearch]);

  const handleSelectPrimary = (member) => {
    const label = `${member.first_name} ${member.surname}`;
    setForm((prev) => ({ ...prev, primary_member_id: member.id, primaryLabel: label }));
    setPrimarySearch(label);
    setPrimarySelectedLabel(label);
    setPrimaryResults([]);
  };

  const handleSelectSpouse = (member) => {
    const label = `${member.first_name} ${member.surname}`;
    setForm((prev) => ({ ...prev, spouse_member_id: member.id, spouseLabel: label }));
    setSpouseSearch(label);
    setSpouseSelectedLabel(label);
    setSpouseResults([]);
  };

  const handleAdd = () => {
    setForm(emptyForm());
    setPrimarySearch("");
    setPrimarySelectedLabel("");
    setPrimaryResults([]);
    setSpouseSearch("");
    setSpouseSelectedLabel("");
    setSpouseResults([]);
    setEditModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!form.primary_member_id) throw new Error("Pick the member this record is for.");
      if (!form.record_date) throw new Error("Pick a date.");
      setIsSaving(true);
      const headers = authHeaders();
      if (!headers) return;

      const url = form.id
        ? `${API_BASE}/sacramental-records/${form.id}`
        : `${API_BASE}/sacramental-records`;
      const method = form.id ? "PUT" : "POST";

      const payload = {
        type: form.type,
        primary_member_id: form.primary_member_id,
        spouse_member_id: form.type === "marriage" ? form.spouse_member_id || null : null,
        spouse_name:
          form.type === "marriage" && !form.spouse_member_id ? form.spouse_name || null : null,
        record_date: form.record_date,
        officiant_name: form.officiant_name || null,
        certificate_number: form.certificate_number || null,
        location: form.location || null,
        notes: form.notes || null,
      };

      const response = await fetch(url, {
        method,
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Record ${form.id ? "updated" : "created"}`,
        icon: "success",
      });
      fetchRecords();
      setEditModalOpen(false);
    } catch (e) {
      Swal.fire({ title: "Error!", text: e.message, icon: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/sacramental-records/${selectedRecord.id}`, {
        method: "DELETE",
        headers,
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      Swal.fire({ title: "Success!", text: "Record deleted", icon: "success" });
      fetchRecords();
      setDeleteModalOpen(false);
    } catch (e) {
      Swal.fire({ title: "Error!", text: e.message, icon: "error" });
    }
  };

  const handlePrintCertificate = () => window.print();

  return {
    columns,
    rows,
    isLoading,
    error,
    typeFilter,
    setTypeFilter,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    certificateModalOpen,
    setCertificateModalOpen,
    selectedRecord,
    form,
    setForm,
    handleAdd,
    handleSubmit,
    handleDeleteConfirm,
    isSaving,
    primarySearch,
    setPrimarySearch,
    primaryResults,
    handleSelectPrimary,
    spouseSearch,
    setSpouseSearch,
    spouseResults,
    handleSelectSpouse,
    handlePrintCertificate,
  };
}
